import React, { useMemo } from 'react';
import {
    DataGridPro,
    DataGridProProps,
    GridCellParams,
    GridRowParams,
} from '@mui/x-data-grid-pro';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
    buildCustomGrid,
    gridAlignCentre,
    gridDateTimeRenderer
} from "../../../util/grid";
import CustomGridToolbar from "../CustomGridToolbar";
import { GridColumnDef, GridColumnTemplate } from "../grid";
import { GridColumnVisibilityModel } from "@mui/x-data-grid/hooks/features/columns/gridColumnsInterfaces";
import { setColumnVisibility } from "../../../features/cart/cartSlice";
import { useAppDispatch, useAppSelector } from "../../../util/hooks";
import { RootState } from "../../../util/store";
import { GRID_CHECKBOX_SELECTION_FIELD } from "@mui/x-data-grid";

function isRowSelectable(params: GridRowParams): boolean {
    return params.row.deletable;
}

export interface ClassGridProps extends Omit<DataGridProProps, 'columns' | 'checkboxSelection' | 'components'> {
    columnDef: GridColumnDef[];
}

export default function CartGrid(props: ClassGridProps): JSX.Element {
    const {
        columnDef,
        ...rest
    } = props;

    const dispatch = useAppDispatch();
    const columnVisibility = useAppSelector((state: RootState) => state.cart.columnVisibility);

    const columnTemplate:GridColumnTemplate = useMemo(() => {
        return {
            id: {field: 'id', headerName: 'ID', width: 120, hide: true},
            remove: {
                field: 'remove',
                headerName: 'Remove',
                width: 50,
                disableColumnMenu: true,
                disableExport: true,
                disableReorder: true,
                filterable: false,
                resizable: false,
                sortable: false,
                align: gridAlignCentre,
                headerAlign: gridAlignCentre,
                renderHeader: () => (
                    <FontAwesomeIcon size='lg' icon={['fas', 'trash']} title='Remove item from cart'/>
                ),
                renderCell: (params: GridCellParams) => {
                    return (
                        <FontAwesomeIcon
                            size='lg'
                            icon={['fas', 'trash']}
                            style={{opacity: params.row.deletable ? 1.0: 0.5}}
                            title='Remove item from cart'
                        />
                    );
                },
            },
            trackingNumber: {field: 'trackingNumber', headerName: 'Tracking #', width: 150},
            serviceType: {field: 'serviceType', headerName: 'Service Type', width: 180},
            details: {field: 'details', headerName: 'Details', width: 200},
            quantity: {field: 'quantity', headerName: 'Quantity', width: 150},
            requestor: {field: 'requestor', headerName: 'Requestor', width: 200},
            costCenter: {field: 'costCenter', headerName: 'Cost Centre', width: 200},
            customerComment: {field: 'customerComment', headerName: 'Comment', width: 200},
            placedInCartDtime: {
                field: 'placedInCartDtime',
                headerName: 'Added Date/Time',
                width: 200,
                renderCell: gridDateTimeRenderer
            },
        }
    }, []);

    const columns = useMemo(() => {
        return buildCustomGrid(columnDef, columnTemplate);
    }, [columnDef, columnTemplate]);

    const handleColumnVisibilityChange = (model: GridColumnVisibilityModel) => {
        dispatch(setColumnVisibility({
            ...model,
            [GRID_CHECKBOX_SELECTION_FIELD]: true,
            id: false,
        }));
    }

    return (
        <DataGridPro
            {...rest}
            columns={columns}
            checkboxSelection
            isRowSelectable={isRowSelectable}
            components={{
                Toolbar: CustomGridToolbar,
            }}
            columnVisibilityModel={columnVisibility}
            onColumnVisibilityModelChange={handleColumnVisibilityChange}
            localeText={{
                toolbarExport: 'Export grid results',
                toolbarExportLabel: 'Export grid results',
            }}
        />
    );
}
